package com.shreyasjoshi.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import com.shreyasjoshi.models.PortfolioData

@Composable
fun ExperienceSection(isDarkMode: Boolean) {
    var activeCategory by remember { mutableStateOf("all") }
    val experiences = PortfolioData.experiences.filter {
        activeCategory == "all" || it.category == activeCategory
    }

    Section(
        attrs = {
            id("experience")
            classNames(
                "portfolio-section",
                "py-16", "px-4", "sm:px-6", "lg:px-8", "max-w-6xl", "mx-auto", "border-b", "transition-colors",
                if (isDarkMode) "border-white/10" else "border-slate-200"
            )
        }
    ) {
        Div(attrs = { classNames("space-y-10") }) {
            // Header & Filter Row
            Div(attrs = { classNames("flex", "flex-col", "lg:flex-row", "lg:items-end", "justify-between", "gap-4") }) {
                Div(attrs = { classNames("space-y-2") }) {
                    Div(attrs = { classNames("text-xs", "font-mono", "text-[#189AD3]", "tracking-widest", "uppercase") }) {
                        Text("02 // CAREER TIMELINE")
                    }
                    H2(
                        attrs = {
                            classNames(
                                "text-3xl", "font-bold", "tracking-tight",
                                if (isDarkMode) "text-white" else "text-slate-900"
                            )
                        }
                    ) {
                        Text("Work Experience & Production Impact")
                    }
                }

                // Filter Buttons
                Div(attrs = { classNames("experience-filters", "flex", "items-center", "gap-2") }) {
                    FilterButton(
                        label = "All Roles (4)",
                        isSelected = activeCategory == "all",
                        isDarkMode = isDarkMode,
                        onClick = { activeCategory = "all" }
                    )
                    FilterButton(
                        label = "Android & AAOS (2)",
                        isSelected = activeCategory == "android",
                        isDarkMode = isDarkMode,
                        onClick = { activeCategory = "android" }
                    )
                    FilterButton(
                        label = "IT Support (2)",
                        isSelected = activeCategory == "it-support",
                        isDarkMode = isDarkMode,
                        onClick = { activeCategory = "it-support" }
                    )
                }
            }

            // Timeline Items
            Div(attrs = { classNames("space-y-8") }) {
                experiences.forEach { exp ->
                    Div(
                        attrs = {
                            classNames(
                                "p-6", "rounded-sm", "border", "transition-all",
                                if (isDarkMode) "bg-[#0a0a0a] border-white/10 hover:border-white/20"
                                else "bg-slate-50 border-slate-200 hover:border-slate-300"
                            )
                        }
                    ) {
                        // Title Row
                        Div(attrs = { classNames("flex", "flex-col", "lg:flex-row", "lg:items-baseline", "justify-between", "gap-2", "mb-2") }) {
                            Div(attrs = { classNames("experience-title", "flex", "items-center", "gap-3") }) {
                                H3(
                                    attrs = {
                                        classNames(
                                            "text-xl", "font-bold",
                                            if (isDarkMode) "text-white" else "text-slate-900"
                                        )
                                    }
                                ) {
                                    Text(exp.role)
                                }
                                if (exp.category == "android") {
                                    Span(
                                        attrs = {
                                            classNames(
                                                "px-2", "py-0.5", "rounded-sm", "text-[10px]", "font-mono", "font-semibold",
                                                "bg-[#189AD3]/10", "text-[#71C7EC]", "border", "border-[#189AD3]/30"
                                            )
                                        }
                                    ) {
                                        Text("AUTOMOTIVE / ANDROID")
                                    }
                                }
                            }

                            Span(
                                attrs = {
                                    classNames(
                                        "text-xs", "font-mono",
                                        if (isDarkMode) "text-[#71C7EC]" else "text-sky-800"
                                    )
                                }
                            ) {
                                Text("${exp.period} • ${exp.duration}")
                            }
                        }

                        // Company & Location Info
                        Div(
                            attrs = {
                                classNames(
                                    "text-sm", "font-semibold", "mb-1",
                                    if (isDarkMode) "text-white/80" else "text-slate-700"
                                )
                            }
                        ) {
                            Text("${exp.company} • ${exp.location} • ${exp.employmentType}")
                        }

                        if (exp.deploymentNote != null) {
                            Div(
                                attrs = {
                                    classNames(
                                        "text-xs", "italic", "mb-4",
                                        if (isDarkMode) "text-white/40" else "text-slate-500"
                                    )
                                }
                            ) {
                                Text("Deployment: ${exp.deploymentNote}")
                            }
                        }

                        // Highlights List
                        Ul(
                            attrs = {
                                classNames(
                                    "space-y-2", "my-4", "text-xs", "sm:text-sm",
                                    if (isDarkMode) "text-white/70" else "text-slate-600"
                                )
                            }
                        ) {
                            exp.highlights.forEach { h ->
                                Li(attrs = { classNames("flex", "items-start", "gap-2") }) {
                                    Span(attrs = { classNames("text-[#189AD3]", "font-bold") }) { Text("▸") }
                                    Span {
                                        Span(
                                            attrs = {
                                                classNames(
                                                    "font-semibold",
                                                    if (isDarkMode) "text-white/90" else "text-slate-800"
                                                )
                                            }
                                        ) {
                                            Text("${h.category}: ")
                                        }
                                        Text(h.description)
                                    }
                                }
                            }
                        }

                        // Technologies & Projects Row
                        Div(attrs = { classNames("pt-4", "mt-4", "border-t", "flex", "flex-wrap", "items-center", "justify-between", "gap-3", if (isDarkMode) "border-white/10" else "border-slate-200") }) {
                            Div(attrs = { classNames("flex", "flex-wrap", "gap-1.5") }) {
                                exp.technologies.forEach { tech ->
                                    Span(
                                        attrs = {
                                            classNames(
                                                "px-2", "py-0.5", "rounded-sm", "text-[11px]", "font-mono", "border",
                                                if (isDarkMode) "bg-white/5 border-white/10 text-white/70"
                                                else "bg-slate-100 border-slate-200 text-slate-700"
                                            )
                                        }
                                    ) {
                                        Text(tech)
                                    }
                                }
                            }

                            if (exp.projects.isNotEmpty()) {
                                Div(attrs = { classNames("flex", "items-center", "gap-1.5", "text-xs", "font-mono", "text-[#189AD3]") }) {
                                    Text("Projects: ${exp.projects.joinToString(", ")}")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FilterButton(
    label: String,
    isSelected: Boolean,
    isDarkMode: Boolean,
    onClick: () -> Unit
) {
    Button(
        attrs = {
            classNames(
                "px-3", "py-1.5", "rounded-sm", "font-mono", "text-xs", "transition-colors", "border",
                if (isSelected) {
                    "bg-[#189AD3] text-white border-[#189AD3]"
                } else {
                    if (isDarkMode) "border-white/10 text-white/60 hover:text-white bg-white/5"
                    else "border-slate-200 text-slate-600 hover:text-slate-900 bg-white"
                }
            )
            onClick { onClick() }
        }
    ) {
        Text(label)
    }
}


